import * as React from "react";
import * as Rx from "rxjs";
import * as Util from "Util";
import * as Sauce from "Sauce";
import * as _ from "lodash";
import { ValueProcessorFactory, ValueProcessor, ValueSource, ParseResult } from "Sauce/Validation";

/* Sauce Forms are components that wrap native inputs or groups of native inputs.
    Sauce form inputs manage Sauce.ParseResults by processing them with Sauce validators.
    Additionally, have a concept of dirty and clean. Generally, Sauce inputs flush on blur and on enter.

    Display Value:
    While the form is focused: show result.raw.
    When the form is unfocused (therefore clean): result.formatted.
    While the form is dirty the form has a 'dirty' class.
    While !result.valid, the form has an 'invalid' class.  

    Note that result.formatted come from from the ValueProcessor. So it is the ValueProcessor
    that controls the formatted output.

    Flushing:
    They flush (signal a new value to their parent) by calling this.props.flush. 
        Rx.Subject<T>.next is suitable as a flush function. */

type FormElements = HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement;
type KeyVal<T> = [ string, ParseResult<T> ];

export type ChangeHandler = (ev: ValueSource) => void;
export type PressHandler = (ev: React.KeyboardEvent<FormElements>) => void;
export interface FormPropsShared<T> {
    flush: (val: ParseResult<T>) => void;
    processor: ValueProcessor<T>;    
    className?: string;
    autoFocus?: boolean;
    style?: React.CSSProperties;
    value?: ValueSource | ParseResult<T>; /* If you pass a value then you control the form, except when the form is focused */
}
export interface FormState<T> {
    value: ParseResult<T>; 
    dirty: boolean;
    focused: boolean;
}

export class Form<P extends FormPropsShared<T>, S extends FormState<T>, T> extends Sauce.Component<P, S> {
	protected dirty = false;
    protected focus = () => {
        this.setState({ focused: true })
    }
    blur = () => {
        this.flush(this.state.value);
        this.setState({ focused: false });
    }
    protected flush = (next_val: ParseResult<T>) => {
        if (next_val.format_exception) console.warn(next_val.format_exception);
        if (next_val.process_exception) console.warn(next_val.process_exception);
        this.setState({ dirty: false })
        this.props.flush(next_val); 
    }
    /* this one updates the form but doesn't signal that anything has changed.
       Most suitable for onChange */
    update_state_handler<T>(state_key: string, 
                            processor: ValueProcessor<T>): (ev: ValueSource) => void 
    {
        return (ev: ValueSource) => {
            let next_val = processor(ev);
            this.set_value(state_key, next_val);
        }
    }
    
    wrap(handler: ChangeHandler, before?: (ev: ValueSource) => ValueSource, after?: (ev: ValueSource) => void) {
        return (ev: ValueSource) => {
            let _ev = before ? before(ev) : ev;
            handler(_ev);
            after ? after(_ev) : null; 
        }
    }

    displayValue = () => {
        if (this.state.focused || this.state.dirty) {
            return this.state.value.raw;
        }
        
        return this.state.value.formatted;
    }
    makeClasses = () => {
        return (this.props.className ? this.props.className : "") + 
        (this.state.dirty ? " dirty" : "") + 
        (!this.state.value.valid ? " invalid" : "")  + 
        (this.state.focused ? " focused" : "");
    }
    enterdown<T>(fn: (val: React.KeyboardEvent<FormElements>) => void): PressHandler {
        return (ev: React.KeyboardEvent<FormElements>) => {
            if (ev.key == "Enter") {
                fn(ev);
            }
        }    
    }

    parsePropValue(val: ValueSource | ParseResult<T>): ParseResult<T> {
        if (val instanceof ParseResult) {
            return val;
        }
        else {
            return this.props.processor(val);
        }
    }

    initState(props: P): FormState<T> {
        const origval = props.value != null ? this.parsePropValue(props.value) : props.processor("");
        return {
            value: origval,
            dirty: false,
            focused: Boolean(this.props.autoFocus)
        };
    }

    componentWillReceiveProps(props: P) {
        if (!this.state.dirty && props.value) {
            this.setState({ value: this.parsePropValue(props.value) })
        }
    }
    
    shouldComponentRender(nextP: P, nextS: S) {
        let cond = this.state.value.raw !== nextS.value.raw;
        if (!cond) {
            console.log("Don't re-render form", this.state.value.raw, nextS.value.raw);
        }
        return this.state.value.raw !== nextS.value.raw; /* raw doesn't change, then don't re-render; try to avoid messing with text highlighting */
    }
	private set_value(key: string, val: ParseResult<any>): any {
		let state: any = {};
        let prev: any = this.state;

        if (val.raw !== prev[key].raw) {
            state[key] = val;
            this.setState(state);
            this.dirty = true;
        }
	}	
}

export interface FormProps<T> extends FormPropsShared<T> {
}

export interface NumberInputProps extends FormProps<number>{
    default_increment?: number;
}
export interface NumberInputState extends FormState<number>{
    increment: number;
}
export class NumberInput extends Form<NumberInputProps, NumberInputState, number> {
    private on_change: ChangeHandler;
    private keydown: PressHandler;
    constructor(props: NumberInputProps) {
        super(props);
        this.on_change = this.wrap(this.update_state_handler("value", props.processor), (ev) => {
            this.setState({ dirty: true });
            return ev;
        });
        this.keydown = ((ev: React.KeyboardEvent<FormElements>) => {
            if (ev.key == "Enter") {
                this.flush(this.state.value); 
                this.setState({ dirty: false });
            }
            else if (ev.key == "ArrowUp") {
                this.on_change((this.state.value.value + this.state.increment).toString());
            }
            else if (ev.key == "ArrowDown") {
                this.on_change((this.state.value.value - this.state.increment).toString());
            }
        });

        let inc = _.isFinite(props.default_increment) ? props.default_increment : 1;

        this.state = { ...this.initState(props), ...{ increment: inc }};
    }
    
    render() {
        return <input type="text" 
            style={this.props.style}
            autoFocus={this.props.autoFocus}
            className={this.makeClasses()} 
            value={this.displayValue()} 
            onChange={this.on_change}
            onKeyDown={this.keydown}
            onFocus={this.focus}
            onBlur={this.blur}/>
    }
}

export interface CheckboxProps extends FormPropsShared<boolean> {
    initialValue?: boolean | ParseResult<boolean>;
}
interface CheckboxState extends FormState<boolean> {}
export class Checkbox extends Form<CheckboxProps, CheckboxState, boolean> {
    private on_change: ChangeHandler;
    constructor(props: CheckboxProps) {
        super(props);
        this.state = {
            value: props.initialValue instanceof ParseResult? props.initialValue : props.processor(props.initialValue),
            dirty: false,
            focused: this.props.autoFocus
        };
        this.on_change = this.wrap(this.update_state_handler("value", props.processor), undefined, (ev) => {
            let next = props.processor(ev);
            this.flush(next);
            return ev;
        });
        this.state = this.initState(props);
    }

    render() {
        return <input type="checkbox" 
                style={this.props.style}
                className={this.makeClasses()} 
                checked={this.state.value.value !== null ? this.state.value.value : undefined}
                onBlur={this.blur}
                onFocus={this.focus}
                onChange={this.on_change}/>
    }
}

export interface TextAreaProps<T> extends FormProps<T> {}
interface TextAreaState<T> extends FormState<T> {}
export class TextAreaInput<T> extends Form<TextAreaProps<T>, TextAreaState<T>, T> {
    private on_change: ChangeHandler;
    private enter: PressHandler;
    constructor(props: TextAreaProps<T>) {
        super(props);
        this.on_change = this.wrap(this.update_state_handler("value", props.processor), (ev) => {
            this.setState({ dirty: true });
            return ev;
        });
        this.enter = this.enterdown(() => {
            this.flush(this.state.value); 
            this.setState({ dirty: false });
        });
        this.state = this.initState(props);
    }

    render() {
        return <textarea type="text" 
            style={this.props.style}
            autoFocus={this.props.autoFocus}
            className={this.makeClasses()} 
            value={this.displayValue()} 
            onChange={this.on_change}
            onKeyDown={this.enter}
            onFocus={this.focus}
            onBlur={this.blur}/>
    }
}

export class StringTextAreaInput extends TextAreaInput<string> {}
export interface TextInputProps<T> extends FormProps<T> {}
interface TextInputState<T> extends FormState<T> {}
export class TextInput<T> extends Form<TextInputProps<T>, TextInputState<T>, T> {
    private on_change: ChangeHandler;
    private enter: PressHandler;
    constructor(props: TextInputProps<T>) {
        super(props);
        this.on_change = this.wrap(this.update_state_handler("value", props.processor), (ev) => {
            this.setState({ dirty: true });
            return ev;
        });
        this.enter = this.enterdown(() => {
            this.flush(this.state.value); 
            this.setState({ dirty: false });
        });
        this.state = this.initState(props);
    }

    render() {
        return <input type="text"
            style={this.props.style}
            autoFocus={this.props.autoFocus}
            className={this.makeClasses()} 
            value={this.displayValue()} 
            onFocus={this.focus}
            onChange={this.on_change}
            onKeyDown={this.enter}
            onBlur={this.blur}/>
    }
}
export class StringTextInput extends TextInput<string> {}



// WEBPACK FOOTER //
// ./src/code/Sauce/Form.tsx